import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MoreVertical, X, ChevronDown } from "lucide-react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    {
      name: "Knowledge",
      dropdown: [
        { name: "How to Fund Your Business", path: "/knowledge/how-to-fund" },
      ],
    },
    {
      name: "Finance Options",
      dropdown: [
        { name: "Fast Business Cash", path: "/finance/fast-business-cash" },
        { name: "Invoice Finance", path: "/finance/invoice-finance" },
        { name: "Unsecured Business Loans", path: "/finance/unsecured-loans" },
      ],
    },
    {
      name: "Insurance Claims",
      dropdown: [
        { name: "Insurance Companies", path: "/insurance/companies" },
        { name: "Private Clients", path: "/insurance/private-clients" },
      ],
    },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <>
      {/* HEADER */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
        }}
        className={`transition-all duration-300 ${
          scrolled ? "bg-white shadow-lg" : "bg-white/95 backdrop-blur-sm"
        }`}
      >
        <nav className="container-custom px-4">
          <div className="flex items-center justify-between h-20 w-full">

            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-accent-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">FT</span>
              </div>

              <span className="font-heading font-bold text-xl text-primary-900">
                1ST TRUST FINANCES
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">

                  {link.dropdown ? (
                    <button className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 font-medium py-2">
                      <span>{link.name}</span>
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  ) : (
                    <Link
                      to={link.path}
                      className={`font-medium py-2 transition-colors ${
                        location.pathname === link.path
                          ? "text-primary-600"
                          : "text-gray-700 hover:text-primary-600"
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}

                  {/* Dropdown */}
                  {link.dropdown && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <div className="py-2">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-4 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* Desktop Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <Link
                to="/login"
                className="px-5 py-2.5 text-primary-600 font-semibold rounded-lg border-2 border-primary-600 hover:bg-primary-50 transition-all"
              >
                Login
              </Link>

              <Link
                to="/register"
                className="px-5 py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-all"
              >
                Sign Up
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? (
                <X className="w-7 h-7 text-gray-900" />
              ) : (
                <MoreVertical className="w-7 h-7 text-gray-900" />
              )}
            </button>

          </div>
        </nav>
      </header>

      {/* MOBILE MENU */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
        }}
        className={`transition-all duration-300 mobile-menu-only ${
          isMenuOpen ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >

        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        ></div>

        {/* Sidebar */}
        <div
          className={`absolute top-0 left-0 h-full w-80 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto ${
            isMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >

          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100">

            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-primary-600 to-accent-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">FT</span>
              </div>

              <span className="font-heading font-bold text-primary-900 text-lg">
                1ST TRUST FINANCES
              </span>
            </div>

            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-6 h-6 text-gray-800" />
            </button>

          </div>

          {/* Menu Items */}
          <div className="p-6">

            <div className="space-y-5">
              {navLinks.map((link) => (
                <div key={link.name}>

                  {link.path ? (
                    <Link
                      to={link.path}
                      className="block text-gray-800 font-medium text-lg hover:text-primary-600"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <span className="block text-gray-900 font-semibold text-lg">
                      {link.name}
                    </span>
                  )}

                  {link.dropdown && (
                    <div className="pl-4 mt-3 space-y-3 border-l border-gray-100">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block text-gray-600 hover:text-primary-600 text-sm"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="space-y-3 mt-10 pt-6 border-t border-gray-100">

              <Link
                to="/login"
                className="block px-5 py-3 text-center text-primary-600 font-semibold rounded-lg border-2 border-primary-600 hover:bg-primary-50"
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>

              <Link
                to="/register"
                className="block px-5 py-3 text-center bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700"
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
